import { useCallback, useEffect, useMemo, useState } from "react";
import { SaveToPlaylistProps } from "@/app/modules/Playlists/types.ts";
import { useCollections, usePlaylists, useVideos } from "@/hooks";

export const usePlaylistDrawer = ({
  ItemId,
  ItemType,
}: Pick<SaveToPlaylistProps, "ItemId" | "ItemType">) => {
  const { data } = usePlaylists();
  const [initialized, setInitialized] = useState(false);
  const playlistIds = useMemo(
    () => data?.Playlists?.map((p) => p.PlaylistId),
    [data],
  );
  const [playlists, setPlaylists] = useState(new Set<number>());

  useEffect(() => {
    if (!data?.Playlists) return;
    if (initialized) return; // Only run on initial mount, create playlist should not reset drawer

    const newPlaylistsSet = new Set<number>();
    data.Playlists.forEach((playlist) => {
      const itemExists = playlist.Items.some(
        (item) => item.ItemId === ItemId && item.ItemType === ItemType,
      );
      if (itemExists) {
        newPlaylistsSet.add(playlist.PlaylistId);
      }
    });
    setPlaylists(newPlaylistsSet);
    setInitialized(true);
  }, [data?.Playlists, ItemId, ItemType, initialized]);

  const { videoIds, collectionIds } = useMemo(() => {
    if (!data?.Playlists) {
      return { videoIds: [], collectionIds: [] };
    }
    const videoIds: number[] = [];
    const collectionIds: number[] = [];

    data.Playlists.forEach((playlist) => {
      const firstItem = playlist.Items?.[0];
      if (firstItem) {
        if (firstItem.ItemType === "video") {
          videoIds.push(firstItem.ItemId);
        } else if (firstItem.ItemType === "collection") {
          collectionIds.push(firstItem.ItemId);
        }
      }
    });
    return { videoIds, collectionIds };
  }, [data?.Playlists]);
  const { videoById } = useVideos({ ids: videoIds });
  const { collectionById } = useCollections({ ids: collectionIds });

  const getFirstItemInPlaylist = useCallback(
    (playlistId: number) => {
      const playlist = data?.Playlists?.find(
        (p) => p.PlaylistId === playlistId,
      );
      if (!videoById || !collectionById) {
        return undefined;
      }
      if (!playlist || !playlist.Items || playlist.Items.length === 0) {
        return undefined;
      }

      const firstItem = playlist.Items[0];

      if (firstItem.ItemType === "video") {
        return videoById[firstItem.ItemId];
      } else if (firstItem.ItemType === "collection") {
        return collectionById[firstItem.ItemId];
      }

      return undefined;
    },
    [data?.Playlists, videoById, collectionById],
  );

  const isItemCurrentlyInPlaylist = useCallback(
    (playlistId: number) => {
      const playlist = data?.Playlists?.find(
        (p) => p.PlaylistId === playlistId,
      );

      if (!playlist || !playlist.Items || playlist.Items.length === 0) {
        return false;
      }
      return playlist.Items.some(
        (item) => item.ItemId === ItemId && item.ItemType === ItemType,
      );
    },
    [data?.Playlists, ItemId, ItemType],
  );

  const isItemInPlaylist = useCallback(
    (playlistId: number) => {
      return playlists.has(playlistId);
    },
    [playlists],
  );

  const setItemInPlaylist = useCallback(
    (playlistId: number, inPlaylist: boolean) => {
      setPlaylists((prevPlaylists) => {
        const updatedPlaylists = new Set(prevPlaylists);

        if (inPlaylist) {
          updatedPlaylists.add(playlistId);
        } else {
          updatedPlaylists.delete(playlistId);
        }

        return updatedPlaylists;
      });
    },
    [],
  );

  const getPlaylistName = useCallback(
    (playlistId: number) => {
      return (
        data?.Playlists?.find((p) => p.PlaylistId === playlistId)
          ?.PlaylistName ?? ""
      );
    },
    [data?.Playlists],
  );

  return {
    playlistIds,
    getFirstItemInPlaylist,
    isItemCurrentlyInPlaylist,
    isItemInPlaylist,
    setItemInPlaylist,
    getPlaylistName,
  };
};
